import { useCallback, useEffect, useRef, useState } from "react";
import { C, F } from "../theme/tokens";
import { useAccount } from "./useAccount";
import { dataIt } from "./ui";
import { nomeUnico } from "../zipWriter";
import {
  EVENTO_LAVORI, chiaveLavori, chiediRiapertura, concludiInCronologia, dimenticaDallaCronologia,
  esitoStati, leggiCronologia, oraDalMotore, oraScadenza,
} from "../recupero";

/**
 * Barra laterale dell'area di lavoro.
 *
 * C'e' per tutti, anche per chi non ha un account: il pulsante per una
 * nuova anonimizzazione e l'elenco dei lavori recenti, da cui si riapre
 * quello che si e' fatto nell'ultima ora (il motore tiene i documenti circa
 * un'ora, poi li elimina). Chi ha un abbonamento legge l'elenco dal backend,
 * per tutto il team; gli altri lo leggono dalla memoria di questo browser
 * (vedi recupero.js). In fondo il consumo del mese e il proprio nome, o
 * l'accesso per chi non ha un account, e il pulsante che riduce la barra.
 *
 * Ridotta e intera sono LA STESSA barra: cambia solo la larghezza, animata.
 * Il contenuto ha sempre la larghezza piena e la barra lo taglia, quindi
 * niente va a capo e niente si sposta durante l'animazione. Tutto quello
 * che resta visibile da ridotta sta nei primi 72 pixel, centrato sulla
 * stessa colonna: il pittogramma (la stessa immagine del logo, tagliata),
 * i pulsanti, l'avatar, il pulsante per ridurre ed espandere. Le etichette
 * e gli elenchi sfumano. Su desktop parte ridotta e la scelta vale per la
 * scheda aperta; su schermi stretti e' un pannello a scomparsa, sempre intero.
 */

const OBSCURA_LOGO = `${process.env.PUBLIC_URL || ""}/obscura-logo.png`;
const LARGHEZZA = 250;
const LARGHEZZA_RIDOTTA = 72;
/** Bordo sinistro degli elementi della colonna: centrati a 36px, come la barra ridotta. */
const MARGINE = 14;
const LATO = 44;
const LARGHEZZA_RIGA = LARGHEZZA - 2 * MARGINE;
/** Logo: il pittogramma occupa il primo 27% dell'immagine, il testo parte dal 33%. */
const LOGO_LARGO = 150;
const LOGO_ALTO = Math.round((LOGO_LARGO * 830) / 3088);
const TAGLIO_LOGO = 30.1;
const CHIAVE_RIDOTTA = "obscura_sidebar_ridotta";

const DURATA = 280;
const CURVA = "cubic-bezier(0.32, 0.72, 0, 1)";

function movimentoRidotto() {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (_) { return false; }
}

function leggiRidotta() {
  try {
    const v = window.sessionStorage.getItem(CHIAVE_RIDOTTA);
    return v === null ? true : v === "1";
  } catch (_) {
    return true;
  }
}

function ricordaRidotta(v) {
  try { window.sessionStorage.setItem(CHIAVE_RIDOTTA, v ? "1" : "0"); } catch (_) { /* niente memoria */ }
}

/**
 * Etichette ed elenchi: sfumano, e da nascosti escono anche dal focus e
 * dagli screen reader. Espandendo arrivano dopo che la barra ha preso un
 * po' di spazio; riducendo se ne vanno subito.
 */
function dissolvenza(visibile, fermo) {
  if (fermo) return { opacity: visibile ? 1 : 0, visibility: visibile ? "visible" : "hidden" };
  return visibile
    ? { opacity: 1, visibility: "visible", transition: "opacity 180ms ease 100ms, visibility 0s linear 0s" }
    : { opacity: 0, visibility: "hidden", transition: "opacity 110ms ease 0s, visibility 0s linear 110ms" };
}

function dataDa(iso) {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ora(iso) {
  const d = dataDa(iso);
  return d ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
}

function oraDa(ts) {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function salvaBase64(nome, base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const est = nome.split(".").pop()?.toLowerCase();
  const mime = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" }[est] || "application/octet-stream";
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const STATO = {
  in_corso: { testo: "in elaborazione", colore: "#8a5a00" },
  completato: { testo: "pronto", colore: "#1f6b3a" },
  fallito: { testo: "non riuscito", colore: "#b42318" },
  scaduto: { testo: "scaduto", colore: C.textNum },
};

const Ico = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const icone = {
  nuovo: <Ico size={20} d={<path d="M12 5v14M5 12h14" />} />,
  recenti: <Ico d={<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>} />,
  utente: <Ico d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />,
  accedi: <Ico d={<><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></>} />,
  crea: <Ico d={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>} />,
  riduci: <Ico d={<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="m16 15-3-3 3-3" /></>} />,
  espandi: <Ico d={<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="m14 9 3 3-3 3" /></>} />,
  scarica: <Ico size={14} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />,
  riapri: <Ico size={14} d={<><path d="M3 12a9 9 0 1 0 3-6.7" /><polyline points="3 3 3 9 9 9" /></>} />,
};

const etichetta = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textNum, fontFamily: F.b };
const vuoto = { margin: 0, fontSize: 12, color: C.textNum, lineHeight: 1.5, fontFamily: F.b };
const scheda = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.smartWhite };
const titoloScheda = { fontSize: 13, fontWeight: 600, color: C.textTitle, fontFamily: F.b, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const rigaScheda = { fontSize: 11, color: C.textNum, fontFamily: F.b, marginTop: 2, lineHeight: 1.4 };
const azione = (attesa) => ({
  marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8,
  border: `1px solid ${C.smartBlue30}`, background: C.smartBlue20, color: C.smartBlue, fontSize: 12, fontWeight: 600,
  fontFamily: F.b, cursor: attesa ? "wait" : "pointer", opacity: attesa ? 0.7 : 1,
});

const TONI = {
  scuro: { background: C.btnBg, color: "#fff", border: "1px solid transparent", sopra: C.btnBg },
  blu: { background: C.smartBlue, color: "#fff", border: "1px solid transparent", sopra: "#0088c4" },
  tenue: { background: C.smartBlue20, color: C.smartBlue, border: `1px solid ${C.smartBlue30}`, sopra: C.smartBlue30 },
  vuoto: { background: "transparent", color: C.textTitle, border: "1px solid transparent", sopra: C.smartBlue10 },
};

/**
 * Una riga della colonna: icona in un quadrato di 44px sempre allo stesso
 * posto, etichetta che sfuma. Da ridotta la riga e' il quadrato stesso; da
 * intera si allarga verso destra, con la stessa animazione della barra.
 *
 * L'elemento e' sempre lo stesso bottone, anche quando da intera non fa
 * niente (``inattiva``): cambiarlo con un div farebbe sparire l'etichetta
 * di colpo invece di sfumarla. L'etichetta ha la larghezza della riga
 * intera, cosi' i puntini di sospensione non si ricalcolano a ogni frame.
 */
function RigaColonna({ icona, children, onClick, ridotta, fermo, tono = "vuoto", altezza = 40, etichettaAccessibile, titolo, badge, inattiva = false, grassetto = false }) {
  const [sopra, setSopra] = useState(false);
  const t = TONI[tono] || TONI.vuoto;
  return (
    <button
      type="button"
      onClick={inattiva ? undefined : onClick}
      disabled={inattiva}
      aria-label={etichettaAccessibile}
      title={ridotta ? titolo || etichettaAccessibile : undefined}
      onMouseEnter={() => setSopra(true)}
      onMouseLeave={() => setSopra(false)}
      style={{
        position: "relative", display: "flex", alignItems: "center", boxSizing: "border-box",
        marginLeft: MARGINE, width: ridotta ? LATO : LARGHEZZA_RIGA, height: altezza, padding: 0,
        borderRadius: 10, overflow: "hidden", whiteSpace: "nowrap", textAlign: "left",
        background: sopra && !inattiva ? t.sopra : t.background, color: t.color, border: t.border,
        cursor: inattiva ? "default" : "pointer", fontFamily: F.b,
        transition: fermo ? "none" : `width ${DURATA}ms ${CURVA}, background 150ms ease`,
      }}
    >
      <span style={{ width: LATO - 2, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icona}</span>
      <span style={{ width: LARGHEZZA_RIGA - LATO, flexShrink: 0, boxSizing: "border-box", paddingRight: 12, overflow: "hidden", textOverflow: "ellipsis", fontSize: 12, fontWeight: grassetto ? 700 : 600, letterSpacing: grassetto ? "0.08em" : "normal", ...dissolvenza(!ridotta, fermo) }}>
        {children}
      </span>
      {badge}
    </button>
  );
}

function Consumo({ account, onNavigateTo }) {
  const { azienda, consumo } = account;
  if (!azienda.abbonata) {
    return (
      <div>
        <div style={etichetta}>Piano</div>
        <p style={{ ...vuoto, margin: "6px 0 0" }}>
          Nessun abbonamento attivo.{" "}
          <button type="button" onClick={() => onNavigateTo("account")} style={{ background: "none", border: "none", padding: 0, color: C.smartBlue, fontWeight: 600, cursor: "pointer", fontFamily: F.b, fontSize: 12 }}>Attiva un piano</button>
        </p>
      </div>
    );
  }
  const incluse = consumo?.pagine_incluse;
  const usate = consumo?.pagine_usate ?? 0;
  const illimitate = incluse === null || incluse === undefined;
  const quota = illimitate ? 0 : Math.min(1, incluse ? usate / incluse : 0);
  return (
    <div>
      <div style={etichetta}>Pagine del mese</div>
      {illimitate ? (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: C.textTitle, fontFamily: F.b }}>
          <strong>{usate}</strong> elaborate <span style={{ color: C.textNum }}>&middot; illimitate</span>
        </p>
      ) : (
        <>
          <p style={{ margin: "6px 0 6px", fontSize: 13, color: C.textTitle, fontFamily: F.b }}>
            <strong>{usate}</strong> di {incluse} pagine
          </p>
          <div style={{ height: 6, borderRadius: 3, background: C.smartBlue20, overflow: "hidden" }}>
            <div style={{ width: `${Math.round(quota * 100)}%`, height: "100%", background: quota >= 1 ? "#d97706" : C.smartBlue }} />
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 11, color: C.textNum, fontFamily: F.b, lineHeight: 1.4 }}>
            Ne restano {consumo?.pagine_rimanenti ?? 0}{consumo?.finestra_fine ? `, si azzerano il ${dataIt(consumo.finestra_fine)}` : ""}.
            {consumo?.pagine_extra > 0 && <> Oltre le incluse: {consumo.pagine_extra}.</>}
          </p>
        </>
      )}
    </div>
  );
}

/** I lavori del team, dal backend: si scaricano o si riaprono finche' il motore li ha. */
function LavoriDelTeam({ lavori, scaricando, jobIdsAperti, onScarica, onRiapri }) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {lavori.map((l) => {
        const stato = STATO[l.stato] || STATO.in_corso;
        const pronto = l.stato === "completato";
        const inScaricamento = scaricando === l.job_id;
        const aperto = jobIdsAperti.includes(l.job_id);
        return (
          <li key={l.job_id} style={{ ...scheda, borderColor: aperto ? C.smartBlue30 : C.border }}>
            <div style={titoloScheda} title={l.nome_file || ""}>{l.nome_file || "Documento"}</div>
            <div style={rigaScheda}>
              {ora(l.creato_il)}{l.pagine ? ` · ${l.pagine} ${l.pagine === 1 ? "pagina" : "pagine"}` : ""} · <span style={{ color: stato.colore, fontWeight: 600 }}>{stato.testo}</span>
            </div>
            {pronto && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <button type="button" onClick={() => onScarica(l)} disabled={inScaricamento} style={azione(inScaricamento)}>
                  {icone.scarica} {inScaricamento ? "Scaricamento..." : `Scarica, fino alle ${ora(l.scade_il)}`}
                </button>
                {aperto ? (
                  <span style={{ ...rigaScheda, display: "inline-block", marginTop: 8, color: C.smartBlue, fontWeight: 600 }}>Aperto</span>
                ) : (
                  <button type="button" onClick={() => onRiapri(l)} style={azione(false)}>{icone.riapri} Riapri</button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Le anonimizzazioni di questo browser: si riaprono nella pagina, con
 *  anteprime o documenti sbloccati a seconda che siano state pagate. */
function LavoriDelBrowser({ voci, jobIdsAperti, onRiapri }) {
  const chiaveAperta = chiaveLavori(jobIdsAperti);
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {voci.map((voce) => {
        const chiave = chiaveLavori(voce.jobIds);
        const aperta = Boolean(chiaveAperta) && chiave === chiaveAperta;
        const nomi = voce.nomi.length ? voce.nomi : voce.jobIds.map((_, i) => `Documento ${i + 1}`);
        const titolo = nomi.length > 1 ? `${nomi[0]} e ${nomi.length - 1 === 1 ? "un altro" : `altri ${nomi.length - 1}`}` : nomi[0];
        return (
          <li key={chiave} style={{ ...scheda, borderColor: aperta ? C.smartBlue30 : C.border }}>
            <div style={titoloScheda} title={nomi.join(", ")}>{titolo}</div>
            <div style={rigaScheda}>
              {/* In elaborazione: niente scadenza (il motore conta l'ora
                  dalla fine) e niente "Riapri", come per gli abbonati.
                  Quando e' pronto la voce cambia da sola. */}
              {oraDa(voce.ts)} · {voce.inCorso ? (
                <span style={{ color: STATO.in_corso.colore, fontWeight: 600 }}>{STATO.in_corso.testo}</span>
              ) : (
                <><span style={{ color: voce.sessionId ? "#1f6b3a" : "#8a5a00", fontWeight: 600 }}>{voce.sessionId ? "pagati" : "anteprima, da pagare"}</span> · fino alle {oraScadenza(voce)}</>
              )}
            </div>
            {aperta ? (
              <span style={{ ...rigaScheda, display: "inline-block", marginTop: 8, color: C.smartBlue, fontWeight: 600 }}>Aperto nella pagina</span>
            ) : voce.inCorso ? null : (
              <button type="button" onClick={() => onRiapri(voce)} style={azione(false)}>
                {icone.riapri} Riapri
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function Sidebar({ onNuova, onNavigateTo, onRiapri, jobIdsAperti = [], isMobile = false, aperta = false, onChiudi }) {
  const { account, api } = useAccount();
  const abbonata = Boolean(account?.azienda?.abbonata);
  const [lavori, setLavori] = useState([]);
  const [locali, setLocali] = useState(() => leggiCronologia());
  const [scaricando, setScaricando] = useState(null);
  const [errore, setErrore] = useState(null);
  const [ridottaScelta, setRidottaScelta] = useState(leggiRidotta);
  const [fermo] = useState(movimentoRidotto);
  const imposta = (v) => { setRidottaScelta(v); ricordaRidotta(v); };
  // Su telefono il pannello e' sempre intero: si apre e si chiude dalla top bar.
  const ridotta = !isMobile && ridottaScelta;

  // Abbonati: l'elenco del team dal backend. Si ricarica anche quando cambia
  // il profilo (dopo un'anonimizzazione la pagina lo rilegge), cosi' il
  // lavoro nuovo compare senza altro.
  const carica = useCallback(async () => {
    if (!abbonata) return;
    try {
      const r = await api.lavori();
      setLavori(Array.isArray(r?.lavori) ? r.lavori : []);
    } catch (_) { /* l'elenco resta com'e' */ }
  }, [abbonata, account, api]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    if (!abbonata || !lavori.some((l) => l.stato === "in_corso")) return undefined;
    const t = setInterval(carica, 15000);
    return () => clearInterval(t);
  }, [abbonata, lavori, carica]);

  // Tutti gli altri: la memoria di questo browser. Lo storage non avvisa la
  // scheda che scrive, quindi recupero.js manda un evento; l'intervallo fa
  // sparire le voci quando scadono.
  useEffect(() => {
    if (abbonata) return undefined;
    const aggiorna = () => setLocali(leggiCronologia());
    aggiorna();
    window.addEventListener(EVENTO_LAVORI, aggiorna);
    const t = setInterval(aggiorna, 60000);
    return () => { window.removeEventListener(EVENTO_LAVORI, aggiorna); clearInterval(t); };
  }, [abbonata]);

  // I lavori di questo browser ancora in elaborazione, anche se la scheda
  // che li ha inviati e' stata chiusa: lo stato si chiede al backend ogni 15
  // secondi, come per gli abbonati (solo lo stato, niente documenti). Pronti,
  // diventano da riaprire, con l'ora della fine; spariti dal motore o non
  // riusciti, escono dall'elenco.
  const inElaborazione = !abbonata && locali.some((v) => v.inCorso);
  const localiRef = useRef(locali);
  localiRef.current = locali;
  useEffect(() => {
    if (!inElaborazione) return undefined;
    let vivo = true;
    const controlla = async () => {
      for (const voce of localiRef.current.filter((v) => v.inCorso)) {
        const risposte = await Promise.all(voce.jobIds.map(async (id) => {
          try {
            const r = await fetch(`/api/jobs/${encodeURIComponent(id)}/stato`);
            if (r.status === 404) return { stato: "sparito" };
            if (!r.ok) return null;
            const d = await r.json();
            return { stato: d?.status ?? null, ora: oraDalMotore(d?.aggiornato_il) };
          } catch (_) {
            return null; // rete: si riprova al giro dopo
          }
        }));
        if (!vivo) return;
        const esito = esitoStati(risposte.map((x) => x?.stato ?? null));
        if (esito === "pronto") {
          const ore = risposte.map((x) => x?.ora).filter(Number.isFinite);
          // Mai piu' tardi di adesso: un orologio avanti non allunga la vita promessa.
          concludiInCronologia(voce.jobIds, Math.min(ore.length ? Math.max(...ore) : Date.now(), Date.now()));
        } else if (esito === "sparito" || esito === "fallito") {
          dimenticaDallaCronologia(voce.jobIds);
        }
      }
    };
    controlla();
    const t = setInterval(controlla, 15000);
    return () => { vivo = false; clearInterval(t); };
  }, [inElaborazione]);

  const scarica = async (lav) => {
    setScaricando(lav.job_id);
    setErrore(null);
    try {
      const r = await fetch(`/api/jobs/${encodeURIComponent(lav.job_id)}/download`);
      if (r.status === 404) throw new Error("Documento non più disponibile: è passata circa un'ora.");
      if (!r.ok) throw new Error("Scaricamento non riuscito, riprova.");
      const payload = await r.json();
      const items = (Array.isArray(payload?.result?.results) ? payload.result.results : []).filter((i) => i?.content);
      if (!items.length) throw new Error("Nessun documento da scaricare.");
      const usati = new Set();
      for (const item of items) salvaBase64(nomeUnico(item.redacted_filename || lav.nome_file || "documento.pdf", usati), item.content);
    } catch (e) {
      setErrore(e.message);
      carica();
    } finally {
      setScaricando(null);
    }
  };

  // Nella pagina di anonimizzazione i documenti si ricostruiscono sul posto;
  // dalle altre pagine la richiesta viaggia nella memoria della scheda e la
  // pagina di anonimizzazione la esegue appena arriva.
  const riapri = (voce) => {
    if (onRiapri) { onRiapri(voce); return; }
    chiediRiapertura(voce);
    onNavigateTo("anonymize");
  };
  const riapriDelTeam = (l) => riapri({
    jobIds: [l.job_id], sessionId: null,
    ts: dataDa(l.creato_il)?.getTime() ?? Date.now(),
    nomi: l.nome_file ? [l.nome_file] : [],
  });

  if (isMobile && !aperta) return null;
  const utente = account?.utente;
  const conteggio = abbonata ? lavori.length : locali.length;
  const elencoVuoto = conteggio === 0;
  const sfuma = dissolvenza(!ridotta, fermo);

  const contenuto = (
    <aside
      aria-label="Area di lavoro"
      style={{
        width: ridotta ? LARGHEZZA_RIDOTTA : LARGHEZZA, flexShrink: 0, background: C.white, borderRight: `1px solid ${C.border}`,
        boxSizing: "border-box", overflow: "hidden",
        transition: fermo ? "none" : `width ${DURATA}ms ${CURVA}`,
        ...(isMobile
          ? { position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 120, boxShadow: C.shadowLg }
          : { position: "sticky", top: 0, height: "100vh" }),
      }}
    >
      {/* Il contenuto e' largo sempre come la barra intera: la barra ridotta
          lo taglia, niente va a capo durante l'animazione. */}
      <div style={{ width: LARGHEZZA, height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        <div style={{ height: 72, flexShrink: 0, position: "relative", display: "flex", alignItems: "center" }}>
          <button type="button" onClick={() => onNavigateTo("landing")} title="Torna al sito" aria-label="OBSCURA, torna al sito" style={{ background: "none", border: "none", padding: 0, marginLeft: 16, cursor: "pointer", position: "relative", width: LOGO_LARGO, height: LOGO_ALTO }}>
            {/* Una sola immagine, in due parti: il pittogramma resta sempre
                uguale, il testo sfuma quando la barra si riduce. */}
            <img src={OBSCURA_LOGO} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: LOGO_LARGO, height: LOGO_ALTO, display: "block", clipPath: `inset(0 ${100 - TAGLIO_LOGO}% 0 0)` }} />
            <img src={OBSCURA_LOGO} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: LOGO_LARGO, height: LOGO_ALTO, display: "block", clipPath: `inset(0 0 0 ${TAGLIO_LOGO}%)`, ...sfuma }} />
          </button>
          {isMobile && (
            <button type="button" onClick={onChiudi} aria-label="Chiudi" style={{ position: "absolute", right: 12, top: 20, background: "none", border: `1px solid ${C.border}`, borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: C.textNum }}>&times;</button>
          )}
        </div>

        <div style={{ paddingTop: 30, flexShrink: 0 }}>
          <RigaColonna icona={icone.nuovo} onClick={onNuova} ridotta={ridotta} fermo={fermo} tono="scuro" altezza={LATO} etichettaAccessibile="Anonimizza un documento" grassetto>
            ANONIMIZZA
          </RigaColonna>
        </div>

        <div style={{ paddingTop: 22, flexShrink: 0 }}>
          <RigaColonna
            icona={icone.recenti}
            onClick={() => imposta(false)}
            ridotta={ridotta}
            fermo={fermo}
            inattiva={!ridotta}
            etichettaAccessibile="Lavori recenti"
            badge={conteggio > 0 ? (
              <span aria-hidden="true" style={{ position: "absolute", left: 26, top: 3, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, background: C.smartBlue, color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: F.b, display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box", pointerEvents: "none" }}>{conteggio}</span>
            ) : null}
          >
            <span style={etichetta}>Lavori recenti</span>
          </RigaColonna>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: ridotta ? "hidden" : "auto", padding: `8px ${MARGINE}px 12px`, ...sfuma }}>
          {errore && <p role="alert" style={{ margin: "0 0 8px", fontSize: 12, color: "#c0392b", fontFamily: F.b }}>{errore}</p>}
          {elencoVuoto ? (
            <p style={vuoto}>
              Nessun lavoro nell&apos;ultima ora{abbonata ? "" : " su questo browser"}. I documenti restano disponibili per circa un&apos;ora dall&apos;elaborazione, poi vengono eliminati.
            </p>
          ) : abbonata ? (
            <LavoriDelTeam lavori={lavori} scaricando={scaricando} jobIdsAperti={jobIdsAperti} onScarica={scarica} onRiapri={riapriDelTeam} />
          ) : (
            <LavoriDelBrowser voci={locali} jobIdsAperti={jobIdsAperti} onRiapri={riapri} />
          )}
        </div>

        {account && (
          <div style={{ flexShrink: 0, padding: `14px ${MARGINE + 2}px`, borderTop: `1px solid ${C.border}`, ...sfuma }}>
            <Consumo account={account} onNavigateTo={onNavigateTo} />
          </div>
        )}

        <div style={{ flexShrink: 0, borderTop: `1px solid ${C.border}`, padding: "10px 0 8px", display: "flex", flexDirection: "column", gap: 6 }}>
          {utente ? (
            <RigaColonna
              icona={<span style={{ display: "inline-flex", width: 34, height: 34, borderRadius: "50%", background: C.smartBlue20, color: C.smartBlue, alignItems: "center", justifyContent: "center" }}>{icone.utente}</span>}
              onClick={() => onNavigateTo("account")}
              ridotta={ridotta}
              fermo={fermo}
              altezza={48}
              etichettaAccessibile="Area clienti"
              titolo={`${utente.email}: area clienti`}
            >
              <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textTitle, overflow: "hidden", textOverflow: "ellipsis" }} title={utente.email}>{utente.email}</span>
              {utente.nome && <span style={{ display: "block", fontSize: 11, fontWeight: 400, color: C.textNum, overflow: "hidden", textOverflow: "ellipsis" }}>{utente.nome}</span>}
            </RigaColonna>
          ) : (
            <>
              <p style={{ ...vuoto, width: LARGHEZZA_RIGA, margin: `0 0 4px ${MARGINE}px`, whiteSpace: "normal", ...sfuma }}>
                Con un account i lavori restano nell&apos;elenco per tutto il team e le pagine sono comprese nel piano.
              </p>
              <RigaColonna icona={icone.accedi} onClick={() => onNavigateTo("accesso", { next: "anonymize" })} ridotta={ridotta} fermo={fermo} tono="blu" etichettaAccessibile="Accedi">
                Accedi
              </RigaColonna>
              <RigaColonna icona={icone.crea} onClick={() => onNavigateTo("registrazione")} ridotta={ridotta} fermo={fermo} tono="tenue" etichettaAccessibile="Crea un account">
                Crea un account
              </RigaColonna>
            </>
          )}
          {/* Riduci / espandi: sempre qui, in basso a sinistra, in tutti e due gli stati. */}
          {!isMobile && (
            <RigaColonna
              icona={ridotta ? icone.espandi : icone.riduci}
              onClick={() => imposta(!ridotta)}
              ridotta={ridotta}
              fermo={fermo}
              etichettaAccessibile={ridotta ? "Espandi la barra laterale" : "Riduci la barra laterale"}
              titolo="Espandi"
            >
              <span style={{ fontWeight: 500, color: C.textNum }}>Riduci</span>
            </RigaColonna>
          )}
        </div>
      </div>
    </aside>
  );

  if (isMobile) {
    return (
      <>
        <div onClick={onChiudi} aria-hidden="true" style={{ position: "fixed", inset: 0, background: "rgba(24,46,79,0.35)", zIndex: 110 }} />
        {contenuto}
      </>
    );
  }
  return contenuto;
}
